import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { Uuid } from '@/api/types';
import { useAuth } from './auth-context';

const STORAGE_KEY = 'tlp.pharmacy.activePharmacy';
const OVERRIDE_KEY = 'tlp.pharmacy.overridePharmacy';

type ActivePharmacyValue = {
  /** The branch every screen in this app is looking at. Null only before one is resolved. */
  pharmacyId: Uuid | null;
  /** Ids the caller is staff at, straight from the access token via /auth/me. */
  assigned: Uuid[];
  /** True when the account has no assignment and is working through the override below. */
  isOverride: boolean;
  setPharmacyId: (id: Uuid) => void;
  /** For an account with no assignment — see the note on the escape hatch. */
  setOverride: (id: Uuid | null) => void;
};

const ActivePharmacyContext = createContext<ActivePharmacyValue | null>(null);

/**
 * Which pharmacy this app is working inside.
 *
 * Unlike the admin panel, this is **not** a free-text picker. A pharmacy user's
 * branches come from their staff assignment on `/auth/me`, and the overwhelming
 * majority have exactly one — so there is nothing to choose and no way to
 * mistype an id into another business's stock. A second branch turns the header
 * into a switcher; the ids themselves are never typed.
 *
 * The escape hatch: an account with *no* assignment (an admin signing in here
 * to look at a branch, or a staff record whose assignment has not been written
 * yet) may enter an id once. It is stored separately from the normal selection
 * so the two can never be confused, and the server enforces access either way —
 * `requirePharmacyAccess()` runs on every inventory and listing route.
 */
export function ActivePharmacyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<Uuid | null>(() => localStorage.getItem(STORAGE_KEY));
  const [override, setOverrideState] = useState<Uuid | null>(() =>
    localStorage.getItem(OVERRIDE_KEY),
  );

  const assigned = useMemo(() => user?.pharmacyIds ?? [], [user?.pharmacyIds]);

  const setPharmacyId = useCallback((id: Uuid) => {
    setSelected(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const setOverride = useCallback((id: Uuid | null) => {
    setOverrideState(id);
    if (id === null) localStorage.removeItem(OVERRIDE_KEY);
    else localStorage.setItem(OVERRIDE_KEY, id);
  }, []);

  /*
   * A stored selection that is no longer in the assignment must be dropped, not
   * kept: staff moved between branches would otherwise keep loading — and
   * posting stock against — the branch they left.
   */
  useEffect(() => {
    if (assigned.length === 0) return;
    if (selected === null || !assigned.includes(selected)) {
      const first = assigned[0];
      if (first) setPharmacyId(first);
    }
  }, [assigned, selected, setPharmacyId]);

  const pharmacyId = assigned.length > 0 ? selected : override;
  const value = useMemo<ActivePharmacyValue>(
    () => ({
      pharmacyId,
      assigned,
      isOverride: assigned.length === 0 && override !== null,
      setPharmacyId,
      setOverride,
    }),
    [pharmacyId, assigned, override, setPharmacyId, setOverride],
  );

  return <ActivePharmacyContext value={value}>{children}</ActivePharmacyContext>;
}

export function useActivePharmacy(): ActivePharmacyValue {
  const context = use(ActivePharmacyContext);
  if (!context) {
    throw new Error('useActivePharmacy must be used inside <ActivePharmacyProvider>');
  }
  return context;
}

/**
 * The id, guaranteed non-null. Every page under `RequirePharmacy` has one, and
 * this saves each of them writing `pharmacyId ?? ''` into a request path where
 * an empty string would quietly become a 404 instead of a bug.
 */
export function useRequiredPharmacyId(): Uuid {
  const { pharmacyId } = useActivePharmacy();
  if (!pharmacyId) throw new Error('No active pharmacy — this page must sit inside <RequirePharmacy>');
  return pharmacyId;
}
